import React, { useState, useEffect } from 'react';
import { DollarSign, Package, Users, TrendingUp, Loader2, AlertCircle, Clock, CheckCircle, Truck } from 'lucide-react';
import { getOrderStats, OrderStats } from '../../services/orderService';
import { getRetailers } from '../../services/retailerService';

const SalesOverviewTab: React.FC = () => {
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [retailerCount, setRetailerCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [orderStats, retailers] = await Promise.all([
        getOrderStats(),
        getRetailers()
      ]);
      setStats(orderStats);
      setRetailerCount(retailers.length);
    } catch (err: any) {
      setError(err.message || 'Failed to load statistics');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-ember" strokeWidth={1.5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-ember-soft/25 border-l-2 border-ember p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
        <div>
          <p className="text-ember-deep font-medium text-sm">Error loading statistics</p>
          <p className="text-ember text-sm">{error}</p>
          <button onClick={loadStats} className="mt-2 text-ember-deep underline text-sm hover:text-ember transition-colors duration-150">
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const statCards = [
    {
      label: 'Revenue',
      value: `$${stats.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      icon: <DollarSign className="w-5 h-5" strokeWidth={1.5} />,
      bgColor: 'bg-olive/10',
      iconColor: 'text-olive'
    },
    {
      label: 'Orders',
      value: stats.totalOrders.toString(),
      icon: <Package className="w-5 h-5" strokeWidth={1.5} />,
      bgColor: 'bg-steel/10',
      iconColor: 'text-steel'
    },
    {
      label: 'Active retailers',
      value: retailerCount.toString(),
      icon: <Users className="w-5 h-5" strokeWidth={1.5} />,
      bgColor: 'bg-brass/10',
      iconColor: 'text-brass'
    },
    {
      label: 'Avg order value',
      value: `$${stats.averageOrderValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      icon: <TrendingUp className="w-5 h-5" strokeWidth={1.5} />,
      bgColor: 'bg-ember/10',
      iconColor: 'text-ember'
    }
  ];

  const orderStatusBreakdown = [
    { status: 'Pending', count: stats.ordersByStatus.pending, icon: <Clock className="w-4 h-4" strokeWidth={1.5} />, color: 'text-brass', bg: 'bg-brass/10' },
    { status: 'Payment received', count: stats.ordersByStatus.payment_received, icon: <DollarSign className="w-4 h-4" strokeWidth={1.5} />, color: 'text-olive', bg: 'bg-olive/10' },
    { status: 'Processing', count: stats.ordersByStatus.processing, icon: <Package className="w-4 h-4" strokeWidth={1.5} />, color: 'text-steel', bg: 'bg-steel/10' },
    { status: 'Shipped', count: stats.ordersByStatus.shipped, icon: <Truck className="w-4 h-4" strokeWidth={1.5} />, color: 'text-ember-deep', bg: 'bg-ember/5' },
    { status: 'Delivered', count: stats.ordersByStatus.delivered, icon: <CheckCircle className="w-4 h-4" strokeWidth={1.5} />, color: 'text-olive', bg: 'bg-olive/15' }
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-3">— Sales</p>
        <h2 className="font-editorial italic font-light text-3xl text-ink-950 tracking-tight">
          Sales overview.
        </h2>
        <p className="text-ink-700 text-sm mt-1">Key metrics and performance indicators.</p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, idx) => (
          <div key={idx} className="bg-paper-bright border border-ink-200 p-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 ${stat.bgColor} flex items-center justify-center`}>
                <div className={stat.iconColor}>{stat.icon}</div>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">{stat.label}</p>
                <p className="font-editorial italic text-2xl text-ink-950">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Order Status Breakdown */}
      <div className="bg-paper-bright border border-ink-200 p-6 mb-8">
        <h3 className="font-editorial italic text-xl text-ink-950 mb-5 tracking-tight">Order status breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {orderStatusBreakdown.map((item, idx) => (
            <div key={idx} className="text-center">
              <div className={`${item.bg} p-4 mb-2`}>
                <div className={`${item.color} flex items-center justify-center mb-2`}>
                  {item.icon}
                </div>
                <p className="font-editorial italic text-2xl text-ink-950">{item.count}</p>
              </div>
              <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500">{item.status}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions / Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pending Actions */}
        <div className="bg-paper-bright border border-ink-200 p-6">
          <h3 className="font-editorial italic text-xl text-ink-950 mb-5 tracking-tight">Pending actions</h3>
          <div className="space-y-3">
            {stats.ordersByStatus.pending > 0 && (
              <div className="flex items-center justify-between p-3 bg-brass/10 border-l-2 border-brass">
                <span className="text-sm text-ink-700">Orders awaiting payment</span>
                <span className="font-mono font-medium text-brass">{stats.ordersByStatus.pending}</span>
              </div>
            )}
            {stats.ordersByStatus.payment_received > 0 && (
              <div className="flex items-center justify-between p-3 bg-olive/10 border-l-2 border-olive">
                <span className="text-sm text-ink-700">Orders ready to process</span>
                <span className="font-mono font-medium text-olive">{stats.ordersByStatus.payment_received}</span>
              </div>
            )}
            {stats.ordersByStatus.processing > 0 && (
              <div className="flex items-center justify-between p-3 bg-steel/10 border-l-2 border-steel">
                <span className="text-sm text-ink-700">Orders ready to ship</span>
                <span className="font-mono font-medium text-steel">{stats.ordersByStatus.processing}</span>
              </div>
            )}
            {stats.pendingOrders === 0 && stats.ordersByStatus.payment_received === 0 && stats.ordersByStatus.processing === 0 && (
              <div className="flex items-center justify-center p-6 bg-olive/15">
                <div className="text-center">
                  <CheckCircle className="w-8 h-8 text-olive mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-sm text-olive">All caught up. No pending actions.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Performance Summary */}
        <div className="bg-paper-bright border border-ink-200 p-6">
          <h3 className="font-editorial italic text-xl text-ink-950 mb-5 tracking-tight">Performance summary</h3>
          <div className="space-y-5">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500">Fulfillment rate</span>
                <span className="font-mono text-xs text-ink-900">
                  {stats.totalOrders > 0
                    ? Math.round((stats.ordersByStatus.delivered / stats.totalOrders) * 100)
                    : 0}%
                </span>
              </div>
              <div className="w-full bg-ink-100 h-1.5">
                <div
                  className="bg-olive h-1.5 transition-all duration-300 ease-editorial"
                  style={{
                    width: `${stats.totalOrders > 0 ? (stats.ordersByStatus.delivered / stats.totalOrders) * 100 : 0}%`
                  }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500">In transit</span>
                <span className="font-mono text-xs text-ink-900">
                  {stats.totalOrders > 0
                    ? Math.round((stats.ordersByStatus.shipped / stats.totalOrders) * 100)
                    : 0}%
                </span>
              </div>
              <div className="w-full bg-ink-100 h-1.5">
                <div
                  className="bg-ember h-1.5 transition-all duration-300 ease-editorial"
                  style={{
                    width: `${stats.totalOrders > 0 ? (stats.ordersByStatus.shipped / stats.totalOrders) * 100 : 0}%`
                  }}
                ></div>
              </div>
            </div>

            <div className="pt-4 border-t border-ink-200">
              <p className="text-xs text-ink-500">
                Statistics are calculated based on all-time order data.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesOverviewTab;
